// Email ownership check for website sign-up (#666).
//
// A 6-digit code goes out through generateEmailOtp; verifyEmailOtp checks it
// and answers with a single-use `proof`. The server creates the account only
// with that proof (createWebClientAccount, newarc-cloud-functions#392, #766) —
// so the code step is real proof, not a UI gate.

import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase/init";

export const EMAIL_CODE_LENGTH = 6;
/** Matches the server's resend cooldown. */
export const EMAIL_CODE_RESEND_SECONDS = 15;

const SEND_FAILED = "Invio del codice non riuscito. Riprova.";
const GENERIC = "Si è verificato un errore. Riprova.";

/** Same wording as the login code step (#861). */
export const EMAIL_CODE_RESENT_MESSAGE = "Ti abbiamo inviato un nuovo codice. Controlla la tua casella email.";
export const EMAIL_CODE_LIMITED_MESSAGE = "Hai richiesto troppi codici, riprova tra qualche minuto.";

const SEND_MESSAGES = {
  400: "Indirizzo email non valido.",
  429: EMAIL_CODE_LIMITED_MESSAGE,
};

const VERIFY_MESSAGES = {
  400: "Inserisci il codice di 6 cifre.",
  404: "Codice non valido.",
  410: "Il codice è scaduto. Richiedine uno nuovo.",
  429: "Troppi tentativi. Richiedi un nuovo codice.",
};

const call = (name, payload) => httpsCallable(functions, name)(payload);
const statusOf = (data) => Number(data?.status) || 0;

/** Digits only, at most 6: what the code field keeps from typing or pasting. */
export function sanitizeEmailCode(value) {
  return String(value ?? "")
    .replace(/\D+/g, "")
    .slice(0, EMAIL_CODE_LENGTH);
}

/** Sends (or re-sends) the code. Never throws. */
export async function sendEmailCode(email, brand) {
  try {
    const { data } = await call("generateEmailOtp", { email: String(email ?? "").trim(), brand });
    const status = statusOf(data);
    if (status === 200) return { ok: true, status, message: "" };
    return { ok: false, status, message: SEND_MESSAGES[status] || SEND_FAILED };
  } catch (e) {
    return { ok: false, status: 0, message: SEND_FAILED };
  }
}

/**
 * Checks the code. On success returns the single-use `proof` to hand to
 * the account creation. Never throws.
 */
export async function verifyEmailCode(email, code) {
  const otp = sanitizeEmailCode(code);
  const refused = (status, message) => ({ ok: false, status, proof: "", uid: "", message });
  if (otp.length !== EMAIL_CODE_LENGTH) return refused(400, VERIFY_MESSAGES[400]);
  try {
    const { data } = await call("verifyEmailOtp", { email: String(email ?? "").trim(), otp });
    const status = statusOf(data);
    const proof = typeof data?.proof === "string" ? data.proof : "";
    // A 200 without a proof means a backend older than #279: nothing to verify with.
    if (status === 200 && proof) {
      return { ok: true, status, proof, uid: typeof data.uid === "string" ? data.uid : "", message: "" };
    }
    return refused(status, VERIFY_MESSAGES[status] || GENERIC);
  } catch (e) {
    return refused(0, GENERIC);
  }
}
